import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import PromptsEditor from '../components/PromptsEditor';

const api = axios.create({ baseURL: '/api/bahaagents', headers: { 'Content-Type': 'application/json' } });

// ─── Agent Configs ──────────────────────────────────────────────────────────

interface InputField {
  key: string;
  label: string;
  type: 'text' | 'textarea';
  placeholder?: string;
  optional?: boolean;
}

interface AgentConfig {
  label: string;
  description: string;
  buttonText: string;
  inputs: InputField[];
}

const AGENT_CONFIGS: Record<string, AgentConfig> = {
  'seo-checker': {
    label: 'Homepage SEO Checker',
    description: 'Analyze homepage HTML source for entity signals, local SEO, and trust factors.',
    buttonText: 'Analyze SEO',
    inputs: [
      { key: 'html_source', label: 'HTML Source', type: 'textarea', placeholder: 'Paste the full homepage HTML source here. Scripts, styles, and SVGs are auto-stripped. Large pages are processed in chunks automatically.' },
    ],
  },
  'press-release': {
    label: 'Press Release Generator',
    description: 'Generate a 500-650 word, media-ready press release following journalistic standards and AP style formatting.',
    buttonText: 'Generate Press Release',
    inputs: [
      { key: 'topic', label: 'Topic', type: 'text', placeholder: 'e.g., launch, award, milestone, expansion, event, partnership' },
      { key: 'gmb_description', label: 'GMB Description', type: 'textarea', placeholder: 'Full business description including location and services...' },
      { key: 'website_url', label: 'Website URL', type: 'text', placeholder: 'https://example.com' },
      { key: 'specific_url', label: 'Specific Service URL (optional)', type: 'text', placeholder: 'https://example.com/service-city-page', optional: true },
      { key: 'contact_name', label: 'Contact Person Name', type: 'text', placeholder: 'John Smith' },
      { key: 'contact_email', label: 'Contact Email', type: 'text', placeholder: 'john@example.com' },
      { key: 'contact_phone', label: 'Contact Phone', type: 'text', placeholder: '(555) 123-4567' },
      { key: 'announcement_date', label: 'Date of Announcement (optional)', type: 'text', placeholder: 'Leave blank for today\'s date', optional: true },
      { key: 'gmb_listing_url', label: 'GMB Listing URL (optional)', type: 'text', placeholder: 'https://g.co/kgs/...', optional: true },
    ],
  },
  'gmb-post': {
    label: 'GMB Post Generator',
    description: 'Generate policy-compliant, locally optimized Google Business Profile posts.',
    buttonText: 'Generate GMB Post',
    inputs: [
      { key: 'gmb_description', label: 'GMB Business Description', type: 'textarea', placeholder: 'Paste the GMB business description...' },
      { key: 'service_name', label: 'Service Name', type: 'text', placeholder: 'e.g., Drug Rehab' },
      { key: 'service_url', label: 'Service URL', type: 'text', placeholder: 'https://example.com/service' },
      { key: 'full_address', label: 'Full Business Address', type: 'text', placeholder: '123 Main St, City, State ZIP' },
    ],
  },
  'schema-generator': {
    label: 'MedicalWebPage Schema Generator',
    description: 'Generate JSON-LD schema markup for healthcare service pages.',
    buttonText: 'Generate Schema',
    inputs: [
      { key: 'html_source', label: 'HTML Source', type: 'textarea', placeholder: 'Paste the full page HTML source here. Scripts, styles, and SVGs are auto-stripped. Large pages are processed in chunks automatically.' },
    ],
  },
};

// ─── Component ──────────────────────────────────────────────────────────────

export default function BahaAgentsPage() {
  const { agentType } = useParams<{ agentType: string }>();
  const config = agentType ? AGENT_CONFIGS[agentType] : null;

  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);

  // Reset form when agent type changes
  useEffect(() => {
    setInputs({});
    setResult('');
    setError('');
  }, [agentType]);

  const handleGenerate = async () => {
    if (!agentType || !config) return;
    setLoading(true);
    setError('');
    setResult('');
    try {
      const resp = await api.post('/generate', { agent_type: agentType, inputs });
      setResult(resp.data.result);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!config) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto py-12 text-center">
          <h2 className="text-xl font-semibold text-gray-700">Unknown Agent</h2>
          <p className="text-gray-500 mt-2">Select an agent from the sidebar.</p>
        </div>
      </Layout>
    );
  }

  const allFilled = config.inputs.filter((f) => !f.optional).every((f) => inputs[f.key]?.trim());

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{config.label}</h1>
              <p className="text-gray-500 mt-1">{config.description}</p>
            </div>
            <button
              onClick={() => setShowPrompts(!showPrompts)}
              className={showPrompts ? 'btn-primary' : 'btn-secondary'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {showPrompts ? 'Hide Prompts' : 'Edit Prompts'}
            </button>
          </div>
        </div>

        {/* Prompts Editor */}
        {showPrompts && (
          <div className="mb-8">
            <PromptsEditor
              systemType="bahaagents"
              title="Baha Agents Prompts"
              subtitle="Edit the system prompts used by each Baha Agent."
              categories={{
                order: ['analysis', 'generation'],
                names: { analysis: 'Analysis', generation: 'Generation' },
                icons: { analysis: '🔍', generation: '✏️' },
              }}
              onClose={() => setShowPrompts(false)}
            />
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
          {config.inputs.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{field.label}</label>
              {field.type === 'textarea' ? (
                <textarea
                  value={inputs[field.key] || ''}
                  onChange={(e) => setInputs({ ...inputs, [field.key]: e.target.value })}
                  placeholder={field.placeholder}
                  rows={8}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-y font-mono"
                />
              ) : (
                <input
                  type="text"
                  value={inputs[field.key] || ''}
                  onChange={(e) => setInputs({ ...inputs, [field.key]: e.target.value })}
                  placeholder={field.placeholder}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              )}
            </div>
          ))}

          <button
            onClick={handleGenerate}
            disabled={loading || !allFilled}
            className="w-full py-3 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generating...
              </>
            ) : (
              config.buttonText
            )}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">Result</h3>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-green-600">Copied!</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
            <div className="p-6">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">{result}</pre>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
